import { motion, useScroll, useTransform } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { DUR, EASE_OUT_QUINT, GSAP_EASE } from '@/lib/motion';

/**
 * Trail — the shipping cadence.
 *
 * Photographic terrain (terrain.png, kept from the original site) as
 * the background, with a parallax drift on scroll. An SVG overlay
 * carries the trail path + waypoints. GSAP draws the trail in left to
 * right when the section enters the viewport; each waypoint drops in
 * synced to the moment the trail line passes it. Click any waypoint
 * to swap the detail card below.
 *
 * Design rules honored: no extra atmosphere — the photograph IS the
 * atmosphere. Coral coverage is just the trail line + active waypoint
 * + detail card accent.
 */

type Status = 'shipped' | 'current';

type Waypoint = {
  id: string;
  num: string;
  version: string;
  code: string;
  date: string;
  status: Status;
  /** x, y as percent of viewBox (0..100). Tuned to sit on the trail. */
  xPct: number;
  yPct: number;
  bullets: string[];
};

const WAYPOINTS: Waypoint[] = [
  {
    id: 'core',
    num: '01',
    version: 'v0.1',
    code: 'BASE CAMP',
    date: 'shipped 2025-04',
    status: 'shipped',
    xPct: 8,
    yPct: 72,
    bullets: [
      'LZSS v2 with 4096-byte window',
      'Hash-chain match finding',
      'Lazy matching for tighter output',
    ],
  },
  {
    id: 'cli',
    num: '02',
    version: 'v0.1',
    code: 'CLI',
    date: 'shipped 2025-05',
    status: 'shipped',
    xPct: 24,
    yPct: 64,
    bullets: [
      'CLI11 argument parser',
      'compress / extract / list / verify / benchmark',
      'Coloured output, progress bars',
    ],
  },
  {
    id: 'desktop',
    num: '03',
    version: 'v0.1',
    code: 'DESKTOP',
    date: 'shipped 2025-06',
    status: 'shipped',
    xPct: 40,
    yPct: 50,
    bullets: [
      'WPF on .NET 8',
      'Drag-and-drop archive browser',
      'P/Invoke to the native DLL',
    ],
  },
  {
    id: 'threads',
    num: '04',
    version: 'v0.2',
    code: 'ASCENT',
    date: 'shipped 2025-09',
    status: 'shipped',
    xPct: 55,
    yPct: 42,
    bullets: [
      'Fixed-size C++17 thread pool',
      'Parallel compress + CRC',
      'Serial fallback at thread_count ≤ 1',
    ],
  },
  {
    id: 'wasm',
    num: '05',
    version: 'v0.2',
    code: 'WEB',
    date: 'shipped 2025-11',
    status: 'shipped',
    xPct: 70,
    yPct: 30,
    bullets: [
      'Emscripten build, no source rewrites',
      'In-browser compression demo',
      'Same engine, four surfaces',
    ],
  },
  {
    id: 'mobile',
    num: '06',
    version: 'v0.3',
    code: 'SUMMIT',
    date: 'planned',
    status: 'current',
    xPct: 88,
    yPct: 18,
    bullets: [
      '.NET MAUI on Android + iOS',
      'On-device compression',
      'Native file pickers',
    ],
  },
];

const TRAIL_PATH =
  'M 80 405 Q 180 380 280 395 Q 380 420 420 320 Q 460 230 540 250 Q 620 270 680 200 Q 740 130 820 165 Q 880 195 920 110';

export function Trail() {
  const sectionRef = useRef<HTMLElement>(null);
  const sceneRef = useRef<HTMLDivElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const [activeId, setActiveId] = useState<string>('mobile');

  // Parallax on the terrain photo: drifts upward as the section traverses
  // the viewport. The SVG overlay (trail + waypoints) stays planted so
  // the path draw-in remains aligned with the photograph at all times.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  const photoY = useTransform(scrollYProgress, [0, 1], ['0%', '-12%']);
  const photoScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.12]);

  /* GSAP: draw trail in + drop waypoints synced to the line passing them.
   * Reduced-motion: skip animation entirely, set everything to its final
   * visible state so the figure is instantly readable. */
  useEffect(() => {
    if (!pathRef.current || !sceneRef.current) return;

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      gsap.set(pathRef.current, { strokeDasharray: 'none', strokeDashoffset: 0 });
      gsap.set('.wp-mark', { opacity: 1, y: 0 });
      return;
    }

    const path = pathRef.current;
    const length = path.getTotalLength();
    const drawDuration = 2.4; // seconds
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      gsap.set(path, { strokeDasharray: length, strokeDashoffset: length });
      gsap.set('.wp-mark', { opacity: 0, y: 18 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sceneRef.current,
          start: 'top 75%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.to(path, {
        strokeDashoffset: 0,
        duration: drawDuration,
        ease: GSAP_EASE.outPower2,
      });

      // Drop each waypoint at the moment the line "reaches" it.
      WAYPOINTS.forEach((wp) => {
        const t = (wp.xPct / 100) * drawDuration;
        tl.fromTo(
          `.wp-${wp.id}`,
          { opacity: 0, y: 18 },
          { opacity: 1, y: 0, duration: DUR.slow, ease: GSAP_EASE.outQuart },
          t,
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const active = WAYPOINTS.find((w) => w.id === activeId) ?? WAYPOINTS[WAYPOINTS.length - 1];

  return (
    <section
      ref={sectionRef}
      id="trail"
      style={{
        padding: '160px 0',
        background: 'var(--bg-warm)',
        position: 'relative',
      }}
    >
      <div className="container-x">
        <span
          className="t-mono"
          style={{
            fontSize: 'var(--type-xs)',
            color: 'var(--accent)',
            letterSpacing: 'var(--tracking-mono-caps)',
            textTransform: 'uppercase',
          }}
        >
          trail
        </span>
        <h2
          className="t-heading"
          style={{
            marginTop: 16,
            fontSize: 'var(--type-xl)',
            maxWidth: '20ch',
          }}
        >
          The shipping cadence.
        </h2>
        <p
          style={{
            marginTop: 20,
            fontSize: 'var(--type-md)',
            color: 'var(--text-2)',
            maxWidth: '60ch',
            lineHeight: 1.55,
          }}
        >
          Six milestones from base camp to summit. Five shipped, one ahead.
          Tap a waypoint for what landed.
        </p>

        {/* Scene: photo + SVG overlay */}
        <div
          ref={sceneRef}
          style={{
            marginTop: 64,
            position: 'relative',
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 16,
            overflow: 'hidden',
            aspectRatio: '1000 / 562',
          }}
        >
          {/* Terrain photograph with parallax + slight inset zoom */}
          <motion.img
            src={`${import.meta.env.BASE_URL}terrain.png`}
            alt=""
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              y: photoY,
              scale: photoScale,
              transformOrigin: 'center',
            }}
          />

          {/* Vignette + warm wash for legibility of the white SVG strokes */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.22) 100%)',
              pointerEvents: 'none',
            }}
          />

          {/* Corner inscriptions, like the original map margins */}
          <CornerLabel pos="tl">SLOBOZIP · DEVELOPMENT TRAIL · 2025—2026</CornerLabel>
          <CornerLabel pos="tr">45°38′N 14°22′E</CornerLabel>

          {/* SVG overlay — trail + waypoints */}
          <svg
            viewBox="0 0 1000 562"
            preserveAspectRatio="none"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            {/* Glow under the trail */}
            <path
              d={TRAIL_PATH}
              stroke="rgba(255,255,255,0.55)"
              strokeWidth="6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={0.4}
            />
            {/* Main trail path — GSAP draws this in */}
            <path
              ref={pathRef}
              d={TRAIL_PATH}
              stroke="var(--accent)"
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />

            {/* Waypoints */}
            {WAYPOINTS.map((w) => (
              <WaypointPin
                key={w.id}
                wp={w}
                active={w.id === activeId}
                onSelect={() => setActiveId(w.id)}
              />
            ))}
          </svg>
        </div>

        {/* Detail card */}
        <DetailCard wp={active} />
      </div>
    </section>
  );
}

/* ── Pin ────────────────────────────────────────────────────────────── */

function WaypointPin({
  wp,
  active,
  onSelect,
}: {
  wp: Waypoint;
  active: boolean;
  onSelect: () => void;
}) {
  const planned = wp.status === 'current';
  const cx = (wp.xPct / 100) * 1000;
  const cy = (wp.yPct / 100) * 562;
  const radius = active ? 14 : 11;

  return (
    <g
      className={`wp-mark wp-${wp.id}`}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect();
      }}
      tabIndex={0}
      role="button"
      aria-label={`${wp.version} ${wp.code}`}
      style={{ cursor: 'pointer', pointerEvents: 'auto' }}
    >
      {active && (
        <circle
          cx={cx}
          cy={cy}
          r={radius + 14}
          fill="var(--accent)"
          opacity={0.22}
        />
      )}

      {/* White outer halo for legibility against any photo region */}
      <circle
        cx={cx}
        cy={cy}
        r={radius + 4}
        fill="rgba(255,255,255,0.92)"
      />

      {/* Pin head */}
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill={planned ? 'rgba(255,255,255,0.96)' : 'var(--accent)'}
        stroke="var(--accent)"
        strokeWidth={planned ? 2.5 : 1.5}
      />

      {/* Number */}
      <text
        x={cx}
        y={cy + 3.5}
        fontFamily="var(--font-mono)"
        fontSize="11"
        fontWeight={600}
        fill={planned ? 'var(--accent)' : '#fff'}
        textAnchor="middle"
      >
        {wp.num}
      </text>

      {/* Tag below pin (above on the upper-right waypoints to avoid clipping) */}
      <g
        transform={`translate(${cx}, ${
          wp.yPct < 25 ? cy + radius + 22 : cy + radius + 22
        })`}
      >
        <rect
          x={-58}
          y={-12}
          width={116}
          height={20}
          rx={10}
          fill="rgba(255,255,255,0.95)"
        />
        <text
          textAnchor="middle"
          fontFamily="var(--font-mono)"
          fontSize="10"
          fontWeight={500}
          fill="var(--bg-code)"
          letterSpacing="0.4"
          y={2}
        >
          {wp.version} · {wp.code}
        </text>
      </g>
    </g>
  );
}

/* ── Corner label ──────────────────────────────────────────────────── */

function CornerLabel({
  pos,
  children,
}: {
  pos: 'tl' | 'tr' | 'bl';
  children: React.ReactNode;
}) {
  const placement: React.CSSProperties =
    pos === 'tl'
      ? { top: 18, left: 22 }
      : pos === 'tr'
        ? { top: 18, right: 22, textAlign: 'right' }
        : { bottom: 18, left: 22 };

  return (
    <span
      style={{
        position: 'absolute',
        zIndex: 2,
        pointerEvents: 'none',
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        letterSpacing: 2,
        color: 'rgba(255,255,255,0.85)',
        textShadow: '0 1px 6px rgba(0,0,0,0.4)',
        textTransform: 'uppercase',
        ...placement,
      }}
    >
      {children}
    </span>
  );
}

/* ── Detail card ───────────────────────────────────────────────────── */

function DetailCard({ wp }: { wp: Waypoint }) {
  const planned = wp.status === 'current';
  return (
    <motion.div
      key={wp.id}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: DUR.base, ease: EASE_OUT_QUINT }}
      style={{
        marginTop: 32,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        padding: '32px 36px',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        gap: 36,
        alignItems: 'flex-start',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span
          className="t-mono"
          style={{
            fontSize: 'var(--type-xs)',
            color: 'var(--text-3)',
            letterSpacing: 'var(--tracking-mono-caps)',
            textTransform: 'uppercase',
          }}
        >
          waypoint
        </span>
        <span
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--type-2xl)',
            fontWeight: 600,
            letterSpacing: 'var(--tracking-display)',
            lineHeight: 1,
            color: planned ? 'var(--accent)' : 'var(--text)',
          }}
        >
          {wp.num}
        </span>
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <span
            className="t-mono"
            style={{
              fontSize: 'var(--type-xs)',
              color: 'var(--accent)',
              letterSpacing: 'var(--tracking-mono-caps)',
              textTransform: 'uppercase',
            }}
          >
            {wp.version}
          </span>
          <h3
            style={{
              fontSize: 'var(--type-lg)',
              fontWeight: 600,
              letterSpacing: 'var(--tracking-tight)',
              color: 'var(--text)',
            }}
          >
            {wp.code.toLowerCase()}
          </h3>
        </div>

        <ul
          style={{
            marginTop: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            listStyle: 'none',
          }}
        >
          {wp.bullets.map((b, i) => (
            <li
              key={i}
              style={{
                fontSize: 'var(--type-sm)',
                color: 'var(--text-2)',
                lineHeight: 1.55,
                paddingLeft: 20,
                position: 'relative',
              }}
            >
              <span
                aria-hidden
                style={{
                  position: 'absolute',
                  left: 0,
                  top: '0.6em',
                  width: 8,
                  height: 1,
                  background: 'var(--accent)',
                }}
              />
              {b}
            </li>
          ))}
        </ul>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
        <span
          className="t-mono"
          style={{
            fontSize: 10,
            letterSpacing: 'var(--tracking-mono-caps)',
            textTransform: 'uppercase',
            padding: '4px 12px',
            borderRadius: 'var(--radius-pill)',
            color: planned ? 'var(--accent)' : 'var(--text-2)',
            background: planned ? 'var(--accent-bg)' : 'transparent',
            border: `1px solid ${planned ? 'var(--accent-bg)' : 'var(--border-strong)'}`,
          }}
        >
          {planned ? '◌ in progress' : '✓ shipped'}
        </span>
        <span
          className="t-mono"
          style={{
            fontSize: 'var(--type-xs)',
            color: 'var(--text-3)',
            letterSpacing: 0.4,
          }}
        >
          {wp.date}
        </span>
      </div>
    </motion.div>
  );
}
